import { useEffect, useMemo, useState } from 'react';
import type { Post } from '@/lib/firebase/posts';
import { addComment, listComments, subscribeToComments, updateComment, deleteComment } from '@/lib/firebase/comments/queries';
import type { PostComment } from '@/lib/firebase/comments/types';
import { useAuth } from '@/components/AuthProvider';
import Button from '@/components/ui/Button';
import { MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface CommentsSectionProps {
    post: Post;
    allowEngagement: boolean;
    viewerRole: 'supporter' | 'creator';
}

export default function CommentsSection({ post, allowEngagement, viewerRole }: CommentsSectionProps) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [comments, setComments] = useState<PostComment[]>([]);
    const [newText, setNewText] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingText, setEditingText] = useState<string>('');
    const [editingVisibility, setEditingVisibility] = useState<'public' | 'private'>('public');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        let unsub: (() => void) | null = null;
        try {
            unsub = subscribeToComments(
                post.id,
                { includePrivateForUserId: user?.uid ?? undefined },
                (list) => {
                    setComments(list);
                    setLoading(false);
                },
                () => {
                    // Stop loading and show no comments on error
                    setLoading(false);
                }
            );
        } catch (e) {
            setLoading(false);
        }
        return () => { if (unsub) unsub(); };
    }, [post.id, user?.uid]);

    const threads = useMemo(() => {
        const byThread: Record<string, PostComment[]> = {};
        comments.forEach((c) => {
            byThread[c.threadId] = byThread[c.threadId] || [];
            byThread[c.threadId].push(c);
        });
        // sort within threads
        Object.values(byThread).forEach((arr) => arr.sort((a, b) => (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0)));
        // only show public threads plus private threads where viewer participates
        return byThread;
    }, [comments]);

    const handleAdd = async () => {
        if (!user?.uid) {
            toast.error(t('posts.comments.signInToComment'));
            return;
        }
        if (!allowEngagement) return;
        if (!newText.trim()) return;
        try {
            await addComment({
                postId: post.id,
                creatorId: post.userId,
                authorId: user.uid,
                authorDisplayName: user.displayName || user.email || 'Supporter',
                authorRole: viewerRole,
                text: newText.trim(),
                visibility: isPrivate ? 'private' : 'public',
            });
            setNewText('');
            setIsPrivate(false);
        } catch (e: any) {
            console.error('addComment error', e);
            toast.error(e?.message || t('posts.comments.failedToAdd'));
        }
    };

    const handleReply = async (threadId: string, text: string, visibility: 'public' | 'private') => {
        if (!user?.uid) return;
        if (!text.trim()) return;
        try {
            await addComment({
                postId: post.id,
                creatorId: post.userId,
                authorId: user.uid,
                authorDisplayName: user.displayName || user.email || (viewerRole === 'creator' ? 'Creator' : 'Supporter'),
                authorRole: viewerRole,
                text: text.trim(),
                visibility,
                threadId,
                parentId: threadId,
            });
        } catch (e: any) {
            console.error('reply addComment error', e);
            toast.error(e?.message || t('posts.comments.failedToRespond'));
        }
    };

    if (!allowEngagement) {
        return null;
    }

    return (
        <div className="mt-4 border-t pt-4">
            <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">Comments</span>
            </div>

            {/* Top add form removed per request */}

            {/* Threads */}
            <div className="space-y-4">
                {Object.entries(threads).map(([threadId, items]) => {
                    const root = items.find((c) => c.parentId === null) || items[0];
                    const visibility = root.visibility;
                    return (
                        <div key={threadId} className="border rounded-md p-3">
                            <div className="mb-2">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm font-medium text-gray-800">{root.authorDisplayName || (root.authorRole === 'supporter' ? 'Supporter' : 'Creator')}</div>
                                    <div className="text-xs text-gray-500">{root.createdAt?.toDate ? root.createdAt.toDate().toLocaleString() : ''}</div>
                                </div>
                                {editingId === root.id ? (
                                    <div className="mt-2 space-y-2">
                                        <textarea className="w-full border rounded-md p-2 text-sm" rows={3} value={editingText} onChange={(e) => setEditingText(e.target.value)} />
                                        <div className="flex items-center justify-between">
                                            <label className="flex items-center gap-2 text-sm text-gray-600">
                                                <input type="checkbox" checked={editingVisibility === 'private'} onChange={(e) => setEditingVisibility(e.target.checked ? 'private' : 'public')} />
                                                Private note
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>Cancel</Button>
                                                <Button size="sm" onClick={async () => {
                                                    try {
                                                        await updateComment(post.id, root.id, { text: editingText, visibility: editingVisibility });
                                                        setEditingId(null);
                                                    } catch (e: any) {
                                                        toast.error(e?.message || 'Failed to update');
                                                    }
                                                }}>Save</Button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-sm text-gray-700 whitespace-pre-wrap">{root.text}</div>
                                        <div className="mt-1 text-xs text-gray-500">{visibility === 'private' ? 'Private' : 'Public'}</div>
                                        {(user?.uid === root.authorId) && (
                                            <div className="mt-2 flex items-center gap-3 text-xs text-gray-600">
                                                <button className="hover:underline" onClick={() => { setEditingId(root.id); setEditingText(root.text); setEditingVisibility(root.visibility); }}>Edit</button>
                                                <button className="hover:underline" onClick={async () => {
                                                    try { await deleteComment(post.id, root.id); } catch (e: any) { toast.error(e?.message || 'Failed to delete'); }
                                                }}>Delete</button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            {/* Replies */}
                            <div className="pl-3 border-l space-y-2">
                                {items
                                    .filter((c) => c.parentId)
                                    .map((c) => (
                                        <div key={c.id} className="text-sm">
                                            <div className="flex items-center justify-between">
                                                <div className="font-medium text-gray-800">{c.authorDisplayName || (c.authorRole === 'supporter' ? 'Supporter' : 'Creator')}</div>
                                                <div className="text-xs text-gray-500">{c.createdAt?.toDate ? c.createdAt.toDate().toLocaleString() : ''}</div>
                                            </div>
                                            {editingId === c.id ? (
                                                <div className="mt-2 space-y-2">
                                                    <textarea className="w-full border rounded-md p-2 text-sm" rows={2} value={editingText} onChange={(e) => setEditingText(e.target.value)} />
                                                    <div className="flex items-center justify-between">
                                                        <label className="flex items-center gap-2 text-sm text-gray-600">
                                                            <input type="checkbox" checked={editingVisibility === 'private'} onChange={(e) => setEditingVisibility(e.target.checked ? 'private' : 'public')} />
                                                            Private note
                                                        </label>
                                                        <div className="flex items-center gap-2">
                                                            <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>Cancel</Button>
                                                            <Button size="sm" onClick={async () => {
                                                                try {
                                                                    await updateComment(post.id, c.id, { text: editingText, visibility: editingVisibility });
                                                                    setEditingId(null);
                                                                } catch (e: any) {
                                                                    toast.error(e?.message || 'Failed to update');
                                                                }
                                                            }}>Save</Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="text-gray-700 whitespace-pre-wrap">{c.text}</div>
                                                    {(user?.uid === c.authorId) && (
                                                        <div className="mt-1 flex items-center gap-3 text-xs text-gray-600">
                                                            <button className="hover:underline" onClick={() => { setEditingId(c.id); setEditingText(c.text); setEditingVisibility(c.visibility); }}>Edit</button>
                                                            <button className="hover:underline" onClick={async () => { try { await deleteComment(post.id, c.id); } catch (e: any) { toast.error(e?.message || 'Failed to delete'); } }}>Delete</button>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    ))}
                            </div>
                            {/* Reply box for creator (and for supporter on private/public thread if they participate) */}
                            {viewerRole === 'creator' && (
                                <ReplyBox onSubmit={(text) => handleReply(threadId, text, visibility)} />
                            )}
                        </div>
                    );
                })}
                {loading && <div className="text-sm text-gray-500">Loading comments...</div>}
                {!loading && Object.keys(threads).length === 0 && (
                    <div className="text-sm text-gray-500">No comments yet.</div>
                )}
                {/* Add comment at the bottom */}
                {viewerRole === 'supporter' && (
                    <div className="mt-4">
                        <textarea
                            value={newText}
                            onChange={(e) => setNewText(e.target.value)}
                            placeholder={t('posts.comments.writeComment')}
                            className="w-full border rounded-md p-2 text-sm"
                            rows={3}
                        />
                        <div className="mt-2 flex items-center justify-between">
                            <label className="flex items-center gap-2 text-sm text-gray-600">
                                <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />
                                {t('posts.comments.sendAsPrivate')}
                            </label>
                            <Button size="sm" onClick={handleAdd}>
                                <Send className="h-4 w-4 mr-1" /> {t('posts.comments.post')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ReplyBox({ onSubmit }: { onSubmit: (text: string) => void }) {
    const [text, setText] = useState('');
    return (
        <div className="mt-3">
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Write a response"
                className="w-full border rounded-md p-2 text-sm"
                rows={2}
            />
            <div className="mt-2 flex justify-end">
                <Button size="sm" onClick={() => { onSubmit(text); setText(''); }}>
                    <Send className="h-4 w-4 mr-1" /> Respond
                </Button>
            </div>
        </div>
    );
}


